/* eslint-disable no-unused-vars */
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/ClaimResult.css";
import {
  ChevronDown,
  ChevronUp,
  Share2,
  X,
  Facebook,
  Instagram,
  Copy,
  Check,
  ExternalLink,
} from "lucide-react";
import RadarEvidenceBlock from "../components/RadarEvidenceBox";
import SpeechEvidenceCard from "../components/SpeechEvidenceCard";
import AiSummaryPanel from "../components/AISummaryPanel";
import VoteEvidenceCard from "../components/VoteEvidenceCard";
import shareToSocial from "../utils/socialSharing";

// Social Media Copy Dialog Component
const SocialCopyDialog = ({
  platform,
  text,
  onClose,
  onOpenShare = null,
  shareButtonLabel = null,
}) => {
  const [copied, setCopied] = useState(false);
  const textRef = useRef(null);

  const handleCopy = () => {
    if (textRef.current) {
      textRef.current.select();
      document.execCommand("copy");
      setCopied(true);

      // Reset after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  // Determine dialog title and instructions based on platform
  const getDialogTitle = () => {
    switch (platform) {
      case "instagram":
        return "Share to Instagram";
      case "facebook":
        return "Share to Facebook";
      default:
        return "Share Content";
    }
  };

  const getInstructions = () => {
    switch (platform) {
      case "instagram":
        return (
          <ol>
            <li>The image has been downloaded to your device</li>
            <li>Copy the text below</li>
            <li>Open Instagram and create a new post</li>
            <li>Select the downloaded image</li>
            <li>Paste the copied text as your caption</li>
          </ol>
        );
      case "facebook":
        return (
          <ol>
            <li>The image has been downloaded to your device</li>
            <li>Copy the text below</li>
            <li>Click "Open Facebook" or open Facebook in your browser</li>
            <li>Create a new post</li>
            <li>Upload the downloaded image</li>
            <li>Paste the copied text</li>
          </ol>
        );
      default:
        return (
          <ol>
            <li>Copy the text below</li>
            <li>Use it on your preferred platform</li>
          </ol>
        );
    }
  };

  return (
    <div className="copy-dialog-overlay">
      <div className="copy-dialog">
        <h3>{getDialogTitle()}</h3>
        <div className="copy-dialog-content">
          <p>
            {platform === "instagram"
              ? "Instagram doesn't support direct sharing from the web. Follow these steps:"
              : "Follow these steps to share on Facebook:"}
          </p>

          {getInstructions()}

          <div className="copy-text-container">
            <textarea
              ref={textRef}
              readOnly
              value={text}
              className="copy-textarea"
            />
            <button className="copy-button" onClick={handleCopy}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copied!" : "Copy Text"}
            </button>
          </div>

          <div className="copy-dialog-footer">
            {onOpenShare && (
              <button className="share-directly-button" onClick={onOpenShare}>
                <ExternalLink size={16} />
                {shareButtonLabel ||
                  `Open ${
                    platform.charAt(0).toUpperCase() + platform.slice(1)
                  }`}
              </button>
            )}
            <button className="dialog-button" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ClaimResult({
  claim,
  verdict,
  records,
  speech_stats,
  vote_stats,
  counts,
  speechEvidenceData,
  vote_records,
  searchId,
  resultData, // Full resultData object
  setResultData
}) {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedSpeechIndex, setExpandedSpeechIndex] = useState(null);
  
  const [showExplainer, setShowExplainer] = useState(true);
  const [activeRadarSection, setActiveRadarSection] = useState(null);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [shareAction, setShareAction] = useState("");
  const [showSocialDialog, setShowSocialDialog] = useState(false);
  const [socialDialogProps, setSocialDialogProps] = useState({
    platform: "",
    text: "",
    onOpenShare: null,
    shareButtonLabel: null,
  });
  const navigate = useNavigate();
  const handleBackClick = () => {
    setResultData(null)
    navigate('/ask'); // Navigate to the home page (Claim Check page)
  };

  // Safe default values to prevent rendering errors
  const safeStats = speech_stats || {
    clarity: "No data",
    clarity_score: 0,
    language_strength: "No data",
    language_strength_score: 0,
    context_match: "No data",
    context_match_score: 0,
    alignment_with_claim: "No data",
    alignment_with_claim_score: 0,
  };

  const safeVoteStats = vote_stats || {
    consistent_vote: 0,
    inconsistent_vote: 0,
    overall_vote_consistency: 0,
  };

  const radarEvidenceData = [
    {
      id: "speech-clarity",
      icon: "🗣️",
      title: "Speech Clarity",
      summary: safeStats.clarity,
    },
    {
      id: "language-strength",
      icon: "💬",
      title: "Language Strength",
      summary: safeStats.language_strength,
    },
    {
      id: "context-match",
      icon: "🔍",
      title: "Context Match",
      summary: safeStats.context_match,
    },
    {
      id: "vote-consistency",
      icon: "🧮",
      title: "Vote Consistency",
      summary: `Consistent votes: ${safeVoteStats.consistent_vote}, Inconsistent: ${safeVoteStats.inconsistent_vote}`,
    },
    {
      id: "speech-alignment",
      icon: "📈",
      title: "Speech Alignment Over Time",
      summary: safeStats.alignment_with_claim,
    },
  ];

  const radarData = [
    { subject: "Speech Clarity", A: safeStats.clarity_score, fullMark: 100 },
    {
      subject: "Language Strength",
      A: safeStats.language_strength_score,
      fullMark: 100,
    },
    {
      subject: "Context Match",
      A: safeStats.context_match_score,
      fullMark: 100,
    },
    {
      subject: "Vote Consistency",
      A: safeVoteStats.overall_vote_consistency,
      fullMark: 100,
    },
    {
      subject: "Speech Alignment",
      A: safeStats.alignment_with_claim_score,
      fullMark: 100,
    },
  ];

  // Function to handle copying link to clipboard
  const handleCopyLink = () => {
    if (!searchId) return;

    const shareUrl = `${window.location.origin}/ask/${searchId}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(shareUrl)
          .then(() => {
            setShareAction("copied");
            setShowShareTooltip(true);
            setTimeout(() => {
              setShowShareTooltip(false);
            }, 2000);
          })
          .catch((err) => {
            console.error("Could not copy text: ", err);
            fallbackCopyTextToClipboard(shareUrl);
          });
      } else {
        fallbackCopyTextToClipboard(shareUrl);
      }
    } catch (err) {
      console.error("Share failed: ", err);
    }
  };

  // Fallback clipboard method
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        setShareAction("copied");
        setShowShareTooltip(true);
        setTimeout(() => {
          setShowShareTooltip(false);
        }, 2000);
      }
    } catch (err) {
      console.error("Fallback: Could not copy text: ", err);
    }

    document.body.removeChild(textArea);
  };

  // Function to share to Twitter/X
  const handleShareToX = async () => {
    if (!searchId) return;

    const shareUrl = `${window.location.origin}/ask/${searchId}`;

    try {
      setShareAction("preparing");
      setShowShareTooltip(true);

      // Call the shareToSocial function with 'twitter' platform
      const result = await shareToSocial(
        "twitter",
        "result-summary", // ID of the element to capture
        claim,
        verdict,
        resultData,
        shareUrl
      );

      if (result.imageDownloaded) {
        setShareAction("image-downloaded");

        // Change tooltip after a delay
        setTimeout(() => {
          setShareAction("sharing");
        }, 1500);

        // Hide tooltip after Twitter opens
        setTimeout(() => {
          setShowShareTooltip(false);
        }, 3000);
      } else {
        setShareAction("sharing");

        // Hide tooltip after a delay
        setTimeout(() => {
          setShowShareTooltip(false);
        }, 2000);
      }
    } catch (error) {
      console.error("Error sharing to X:", error);
      setShareAction("error");
      setShowShareTooltip(true);
      setTimeout(() => {
        setShowShareTooltip(false);
      }, 2000);
    }
  };

  // Function to share to Facebook
  const handleShareToFacebook = async () => {
    if (!searchId) return;

    const shareUrl = `${window.location.origin}/ask/${searchId}`;

    try {
      setShareAction("facebook");
      setShowShareTooltip(true);

      // Call the shareToSocial function with 'facebook' platform
      const result = await shareToSocial(
        "facebook",
        "result-summary", // ID of the element to capture
        claim,
        verdict,
        resultData,
        shareUrl
      );

      // Show dialog with Facebook text and option to open Facebook
      if (result.textToCopy) {
        setSocialDialogProps({
          platform: "facebook",
          text: result.textToCopy,
          onOpenShare: () => {
            shareToSocial("facebook-share", null, null, null, null, shareUrl);
          },
          shareButtonLabel: "Open Facebook",
        });
        setShowSocialDialog(true);
      }

      // Hide tooltip
      setShowShareTooltip(false);
    } catch (error) {
      console.error("Error sharing to Facebook:", error);
      setShareAction("error");
      setShowShareTooltip(true);
      setTimeout(() => {
        setShowShareTooltip(false);
      }, 2000);
    }
  };

  // Function to share to Instagram
  const handleShareToInstagram = async () => {
    if (!searchId) return;

    const shareUrl = `${window.location.origin}/ask/${searchId}`;

    try {
      setShareAction("instagram");
      setShowShareTooltip(true);

      // Call the shareToSocial function with 'instagram' platform
      const result = await shareToSocial(
        "instagram",
        "result-summary", // ID of the element to capture
        claim,
        verdict,
        resultData,
        shareUrl
      );

      // Show the Instagram dialog with text to copy
      if (result.textToCopy) {
        setSocialDialogProps({
          platform: "instagram",
          text: result.textToCopy,
          onOpenShare: null, // Instagram doesn't have direct web sharing
          shareButtonLabel: null,
        });
        setShowSocialDialog(true);
      }

      // Hide tooltip
      setShowShareTooltip(false);
    } catch (error) {
      console.error("Error sharing to Instagram:", error);
      setShareAction("error");
      setShowShareTooltip(true);
      setTimeout(() => {
        setShowShareTooltip(false);
      }, 2000);
    }
  };

  return (
    <main className="claim-result-container">
      <section className="claim-result-inner">
        <header className="result-header">
          <h1>
            Claim: <q>{claim}</q>{" "}
            <span className={`verdict ${verdict.toLowerCase()}`}>
              {verdict}
            </span>
          </h1>
          

          {searchId && (
            <div className="share-buttons-container">
              <div className="share-buttons">
                <button className="share-button" onClick={handleCopyLink}>
                  <Share2 size={18} />
                  <span>Copy Link</span>
                </button>
                <button
                  className="share-button twitter-button"
                  onClick={handleShareToX}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  <span>Twitter</span>
                </button>
                <button
                  className="share-button facebook-button"
                  onClick={handleShareToFacebook}
                >
                  <Facebook size={18} />
                  <span>Facebook</span>
                </button>
                <button
                  className="share-button instagram-button"
                  onClick={handleShareToInstagram}
                >
                  <Instagram size={18} />
                  <span>Instagram</span>
                </button>
                {showShareTooltip && (
                  <div className="share-tooltip">
                    {shareAction === "copied" && "Link copied to clipboard!"}
                    {shareAction === "preparing" && "Generating image..."}
                    {shareAction === "image-downloaded" && "Image downloaded!"}
                    {shareAction === "sharing" && "Opening Twitter..."}
                    {shareAction === "facebook" &&
                      "Preparing Facebook content..."}
                    {shareAction === "instagram" &&
                      "Preparing Instagram content..."}
                    {shareAction === "error" && "Error sharing. Try again."}
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="explanation">
            This claim was analyzed based on relevant parliamentary speeches and
            voting records. Below are the findings.
          </p>

          <button
            className="logic-button"
            onClick={() => setShowExplainer(!showExplainer)}
          >
            {showExplainer ? "Hide Verdict Logic" : "Show Me the Logic"}
          </button>
        </header>

        {/* This is the element we'll capture for sharing */}
        <div id="result-summary">
          {showExplainer && (
            <section className="verdict-explainer">
              <AiSummaryPanel radarData={radarData} counts={counts} />

              {radarEvidenceData.map((block) => (
                <RadarEvidenceBlock key={block.id} {...block} />
              ))}

              {activeRadarSection && (
                <div className="radar-explainer">
                  <h4>{activeRadarSection}</h4>
                  <p>
                    {activeRadarSection === "Speech Clarity"
                      ? "Measures how clearly the AI interpreted key points in speeches."
                      : activeRadarSection === "Language Strength"
                      ? "Represents the emphasis and assertiveness in the political language used."
                      : activeRadarSection === "Context Match"
                      ? "Assesses how well the speech matches the claim's context."
                      : activeRadarSection === "Vote Consistency"
                      ? "Reflects how often the politician's votes align with the AI-determined position."
                      : activeRadarSection === "Speech Alignment"
                      ? "Proportion of speeches supporting the claim sentiment."
                      : ""}
                  </p>
                </div>
              )}
            </section>
          )}
        </div>

        {speechEvidenceData && speechEvidenceData.length > 0 && (
          <>
            <h2>💬 Speech Extract</h2>
            {speechEvidenceData.map((item, index) => (
              <SpeechEvidenceCard key={index} {...item} />
            ))}
          </>
        )}

        {vote_records && vote_records.length > 0 && (
          <>
            <h2>📜 Voting Records</h2>
            {vote_records.map((item, index) => (
              <VoteEvidenceCard key={index} {...item} />
            ))}
          </>
        )}
       <div className="cr-back-button-container">
            <button 
              className="cr-back-button" 
              onClick={handleBackClick}
              aria-label="Back to claim check"
            >
              ← Back to Claim Check
            </button>
          </div>
      </section>
     

      {/* Social Media Copy Dialog */}
      {showSocialDialog && (
        <SocialCopyDialog
          platform={socialDialogProps.platform}
          text={socialDialogProps.text}
          onClose={() => setShowSocialDialog(false)}
          onOpenShare={socialDialogProps.onOpenShare}
          shareButtonLabel={socialDialogProps.shareButtonLabel}
        />
      )}
    </main>
  );
}
